import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Config } from "./config";
import OAuthInfo from "@arcgis/core/identity/OAuthInfo";
import { ArcGISUserToken, CreateArcGISUserTokenRequest, GetArcGISUserTokenRequest, GisServiceList, GisToken, ArcGISFeatureService, ArcGISFeatureLayer, ArcGISLayerDrawingInfo, ArcGISOrganizationContent, ArcGISSearchResponse, ArcGISWebMap, ArcGISMapService, ArcGISPaginatedSearchResult, ArcGISSearchParams } from "../models/gis-service-list";
import { ArcGISWebMapData, ArcGISServiceMetadata } from "../models/arcgis-types";
import { GIS_SERVICES_LIST, GIS_USER_TOKEN, GIS_USER_EXPIRES_UTC, ARCGIS_USERNAME, ARCGIS_USER_FULLNAME, ARCGIS_USER_ORG_ID, ARCGIS_ORG_NAME } from "app/main/main-resolver.service";
import { ARCGIS_CONSTANTS, normalizePortalUrl } from "../constants/arcgis-constants";

export interface UserAuthTokenInfo {
    access_token: string,
    expires_in: string,
    refresh_token: string,
    refresh_token_expires_in: string,
    username?: string,
}

@Injectable()
export class ArcGISUserService {
    codeVerifier: string = '';
    codeChallenge: string = '';
    public arcGISUserTokenChanged = new BehaviorSubject<ArcGISUserToken>(null);
    public gisServicesListChanged = new BehaviorSubject<Array<GisServiceList>>(null);
    public featureLayersChanged = new BehaviorSubject<ArcGISFeatureLayer[]>(null);
    public webMapsChanged = new BehaviorSubject<ArcGISWebMap[]>(null);
    public mapServicesChanged = new BehaviorSubject<ArcGISMapService[]>(null);
    public organizationContentChanged = new BehaviorSubject<ArcGISOrganizationContent>(null);

    constructor(
        private apiHttp: HttpClient,
    ) { 
        this.generateCodeChallenge();
    }

    private isValidArcGISToken(): boolean {
        const token: string | null = sessionStorage.getItem(GIS_USER_TOKEN);
        return !!token;
    }

    public getPortalUrl(): string {
        const servicesListJson = sessionStorage.getItem(GIS_SERVICES_LIST);
        if (servicesListJson) {
            try {
                const servicesList: GisServiceList[] = JSON.parse(servicesListJson);
                const identityService = servicesList?.find(
                    (s: GisServiceList) => s.serviceType === ARCGIS_CONSTANTS.SERVICE_TYPES.IDENTITY
                );
                if (identityService?.url) {
                    return normalizePortalUrl(identityService.url);
                }
            } catch {
                return ARCGIS_CONSTANTS.DEFAULT_PORTAL_URL;
            }
        }
        return ARCGIS_CONSTANTS.DEFAULT_PORTAL_URL;
    }

    private getIdentityService(): { url: string; appId: string } | null {
        const servicesListJson = sessionStorage.getItem(GIS_SERVICES_LIST);
        if (servicesListJson) {
            try {
                const servicesList: GisServiceList[] = JSON.parse(servicesListJson);
                const identityService = servicesList?.find(
                    (s: GisServiceList) => s.serviceType === ARCGIS_CONSTANTS.SERVICE_TYPES.IDENTITY
                );
                if (identityService) {
                    return {
                        url: normalizePortalUrl(identityService.url),
                        appId: identityService.appId || ''
                    };
                }
            } catch {
                return null;
            }
        }
        return null;
    }

    public deleteUserCredToken(enterpriseUrl?: string | null, customerId?: number): Observable<boolean> {
        const params: string[] = [];
        if (enterpriseUrl) {
            params.push(`${ARCGIS_CONSTANTS.QUERY_PARAMS.ENTERPRISE_URL}=` + encodeURIComponent(enterpriseUrl));
        }
        if (enterpriseUrl && customerId) {
            params.push(`${ARCGIS_CONSTANTS.QUERY_PARAMS.CUSTOMER_ID}=${customerId}`);
        }
        const urlParams = params.length > 0 ? '?' + params.join('&') : '';
        return this.apiHttp.delete<boolean>(Config.urls.ArcGISUserServices + ARCGIS_CONSTANTS.API_PATHS.TOKEN + urlParams).pipe(
            map((response: boolean) => {
                const newResponse = !!response;
                if (newResponse) {
                    sessionStorage.removeItem(GIS_USER_TOKEN);
                    sessionStorage.removeItem(GIS_USER_EXPIRES_UTC);
                    sessionStorage.removeItem(ARCGIS_USERNAME);
                    sessionStorage.removeItem(ARCGIS_USER_FULLNAME);
                    sessionStorage.removeItem(ARCGIS_USER_ORG_ID);
                    sessionStorage.removeItem(ARCGIS_ORG_NAME);

                    this.arcGISUserTokenChanged.next(null);
                    this.featureLayersChanged.next([]);
                    this.organizationContentChanged.next(null);
                }
                return newResponse;
            }),
        );
    }

    // This method is called when the user has successfully logged into arcgis and the authorization 'code'
    // is available to generate a token for the user to be saved in the database.
    public async generateArcGISUserAuthToken(gisOAuthInfo: OAuthInfo, authorizationCode: string): Promise<UserAuthTokenInfo> {
        const identityService = this.getIdentityService();
        const arcgisUrl = identityService?.url || ARCGIS_CONSTANTS.DEFAULT_PORTAL_URL;
        const clientId = identityService?.appId || gisOAuthInfo.appId;
        const redirect_uri = window.location.origin + ARCGIS_CONSTANTS.ROUTES.LOGIN_RESPONSE;

        let newToken: UserAuthTokenInfo = null;
        const tokenEndpoint = new URL(arcgisUrl + ARCGIS_CONSTANTS.OAUTH_ENDPOINTS.TOKEN);
        const queryParams = new Map<string, string>();
        queryParams.set(ARCGIS_CONSTANTS.OAUTH_PARAMS.CLIENT_ID, clientId);
        queryParams.set(ARCGIS_CONSTANTS.OAUTH_PARAMS.GRANT_TYPE, ARCGIS_CONSTANTS.GRANT_TYPES.AUTHORIZATION_CODE);
        queryParams.set(ARCGIS_CONSTANTS.OAUTH_PARAMS.CODE, authorizationCode);
        queryParams.set(ARCGIS_CONSTANTS.OAUTH_PARAMS.REDIRECT_URI, redirect_uri);
        queryParams.set(ARCGIS_CONSTANTS.OAUTH_PARAMS.CODE_VERIFIER, this.codeVerifier);
        queryParams.forEach(function(value, key) {
            tokenEndpoint.searchParams.append(key, value);
        });
        await fetch(tokenEndpoint.toString(), {
            method: 'POST',
            body: null,
            headers: {
                Referer: "https://dev.adsprism.com"
            }
        }).then(function (response) {
            return response.json();
        })
        .then((newSession: UserAuthTokenInfo) => {
            if (newSession && newSession.access_token) {
                newToken = newSession;
                if (newSession.username) {
                    sessionStorage.setItem(ARCGIS_USERNAME, newSession.username);
                }
            }
        });
        return newToken;
    }

    // If you are wanting to generate an authorization code, pass in 'code' for the response_type. Getting a token means passing in 'token'.
    public generateArcGISLoginEndpoint(gisOAuthInfo: OAuthInfo): string {
        const identityService = this.getIdentityService();
        const arcgisUrl = identityService?.url || ARCGIS_CONSTANTS.DEFAULT_PORTAL_URL;
        const clientId = identityService?.appId || gisOAuthInfo.appId;
        const redirect_uri = window.encodeURIComponent(window.location.origin + ARCGIS_CONSTANTS.ROUTES.LOGIN_RESPONSE);
        const authorizationEndpoint = arcgisUrl + ARCGIS_CONSTANTS.OAUTH_ENDPOINTS.AUTHORIZE +
            `?${ARCGIS_CONSTANTS.OAUTH_PARAMS.CLIENT_ID}=` + clientId +
            `&${ARCGIS_CONSTANTS.OAUTH_PARAMS.CODE_CHALLENGE}=` + this.codeChallenge +
            `&${ARCGIS_CONSTANTS.OAUTH_PARAMS.CODE_CHALLENGE_METHOD}=${ARCGIS_CONSTANTS.CODE_CHALLENGE_METHOD}` +
            `&${ARCGIS_CONSTANTS.OAUTH_PARAMS.REDIRECT_URI}=` + redirect_uri +
            `&${ARCGIS_CONSTANTS.OAUTH_PARAMS.RESPONSE_TYPE}=code`;

        return authorizationEndpoint;
    }

    public getGisClientId(): Observable<string> {
        return this.apiHttp.get<string>(Config.urls.ArcGISUserServices + '/clientId')
    }

    public getToken(): string {
        return sessionStorage.getItem(GIS_USER_TOKEN);
    }

    public getUserCredToken(request: GetArcGISUserTokenRequest): void {
        const params: string[] = [];
        if (request?.arcGISEnterpriseDomainUrl) {
            params.push(`${ARCGIS_CONSTANTS.QUERY_PARAMS.ENTERPRISE_URL}=` + encodeURIComponent(request.arcGISEnterpriseDomainUrl));
            params.push(`${ARCGIS_CONSTANTS.QUERY_PARAMS.ENTERPRISE_APP_ID}=` + request.arcGISEnterpriseAppId);
        }
        if (request?.arcGISEnterpriseDomainUrl && request?.customerId) {
            params.push(`${ARCGIS_CONSTANTS.QUERY_PARAMS.CUSTOMER_ID}=${request.customerId}`);
        }
        const urlParams = params.length > 0 ? '?' + params.join('&') : '';
        const httpResponse = this.apiHttp.get<ArcGISUserToken>(Config.urls.ArcGISUserServices + ARCGIS_CONSTANTS.API_PATHS.TOKEN + urlParams);
        httpResponse.subscribe((userGisToken) => {
            this.arcGISUserTokenChanged.next(!!userGisToken ? userGisToken : null);
        });
    }

    public storeUserCredToken(request: CreateArcGISUserTokenRequest, customerId?: number): Observable<ArcGISUserToken> {
        const urlParams = request.arcGISEnterpriseDomainUrl && customerId ? `?${ARCGIS_CONSTANTS.QUERY_PARAMS.CUSTOMER_ID}=${customerId}` : '';
        return this.apiHttp.post<ArcGISUserToken>(Config.urls.ArcGISUserServices + ARCGIS_CONSTANTS.API_PATHS.TOKEN + urlParams, request).pipe(
            map((response: ArcGISUserToken) => {
                this.arcGISUserTokenChanged.next(!!response ? response : null);
                return response;
            }),
        );
    }

    private async generateCodeChallenge() {
        const dec2hex = (dec) => {
            return ('0' + dec.toString(16)).substr(-2)
        }
        const generateRandomString = () => {
            const array = new Uint32Array(56/2);
            window.crypto.getRandomValues(array);
            return Array.from(array, dec2hex).join('');
        }

        const challengeFromVerifier = async (verifier) => {
            // Generate SHA256 buffer
            const encoder = new TextEncoder();
            const data = encoder.encode(verifier);
            const sha256 = await window.crypto.subtle.digest('SHA-256', data);
            // Encode SHA256 as base 64
            let str = "";
            const bytes = new Uint8Array(sha256);
            for (let i = 0; i < bytes.byteLength; i++) {
                str += String.fromCharCode(bytes[i]);
            }
            const challenge = btoa(str)
                .replace(/\+/g, "-")
                .replace(/\//g, "_")
                .replace(/=+$/, "");
            return challenge;
        }

        this.codeVerifier = generateRandomString();

        this.codeChallenge = await challengeFromVerifier(this.codeVerifier);
    }

    public async fetchUserFeatureServices(username: string): Promise<ArcGISFeatureService[]> {
        if (!this.isValidArcGISToken()) {
            return [];
        }

        const token = sessionStorage.getItem(GIS_USER_TOKEN);
        const arcgisUrl = this.getPortalUrl();

        try {
            const response = await fetch(
                `${arcgisUrl}${ARCGIS_CONSTANTS.OAUTH_ENDPOINTS.CONTENT_USERS}/${username}?${ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN}=${token}&${ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT}=${ARCGIS_CONSTANTS.FORMAT.JSON}`
            );
            const data = await response.json();

            const featureServices = data.items?.filter(item => item.type === ARCGIS_CONSTANTS.SERVICE_TYPES.FEATURE_SERVICE) || [];
            return featureServices;
        } catch (error) {
            console.error('Error fetching user feature services:', error);
            return [];
        }
    }

    public async fetchServiceLayers(serviceUrl: string): Promise<ArcGISFeatureLayer[]> {
        if (!this.isValidArcGISToken()) {
            return [];
        }

        const token = sessionStorage.getItem(GIS_USER_TOKEN);

        try {
            const response = await fetch(`${serviceUrl}?${ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN}=${token}&${ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT}=${ARCGIS_CONSTANTS.FORMAT.JSON}`);
            const data = await response.json();
            
            // Fetch thumbnails for each layer
            const layersWithThumbnails = await Promise.all(
                (data.layers || []).map(async (layer) => {
                    const thumbnail = await this.fetchLayerThumbnail(serviceUrl, layer.id);
                    return {
                        ...layer,
                        serviceUrl,
                        thumbnail
                    } as ArcGISFeatureLayer;
                })
            );
            
            return layersWithThumbnails;
        } catch (error) {
            console.error('Error fetching service layers:', error);
            return [];
        }
    }

    private async fetchAndStoreUserInfo(): Promise<boolean> {
        if (!this.isValidArcGISToken()) {
            return false;
        }

        const token = sessionStorage.getItem(GIS_USER_TOKEN);
        const cachedUsername = sessionStorage.getItem(ARCGIS_USERNAME);
        const cachedOrgId = sessionStorage.getItem(ARCGIS_USER_ORG_ID);

        if (cachedUsername && cachedOrgId) {
            return true;
        }

        const arcgisUrl = this.getPortalUrl();
        const apiUrl = `${arcgisUrl}${ARCGIS_CONSTANTS.OAUTH_ENDPOINTS.SELF}?${ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN}=${token}&${ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT}=${ARCGIS_CONSTANTS.FORMAT.JSON}`;

        try {
            const response = await fetch(apiUrl);
            const data = await response.json();

            if (data.error) {
                console.error('ArcGIS API error:', data.error);
                return false;
            }

            if (data.username) {
                sessionStorage.setItem(ARCGIS_USERNAME, data.username);
                sessionStorage.setItem(ARCGIS_USER_FULLNAME, data.fullName || '');
                sessionStorage.setItem(ARCGIS_USER_ORG_ID, data.orgId || '');
                return true;
            }

            console.error('No username in ArcGIS response');
            return false;
        } catch (error) {
            console.error('Error fetching user info from ArcGIS:', error);
            return false;
        }
    }

    private async fetchOrganizationDetails(orgId: string): Promise<string | null> {
        if (!this.isValidArcGISToken()) {
            return null;
        }

        const token = sessionStorage.getItem(GIS_USER_TOKEN);
        const cachedOrgName = sessionStorage.getItem(ARCGIS_ORG_NAME);

        if (cachedOrgName) {
            return cachedOrgName;
        }

        const portalUrl = this.getPortalUrl();

        try {
            const response = await fetch(`${portalUrl}/sharing/rest/portals/${orgId}?${ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN}=${token}&${ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT}=${ARCGIS_CONSTANTS.FORMAT.JSON}`);
            const data = await response.json();

            if (data.error) {
                console.error('ArcGIS organization API error:', data.error);
                return null;
            }

            if (data.name) {
                sessionStorage.setItem(ARCGIS_ORG_NAME, data.name);
                return data.name;
            }

            return null;
        } catch (error) {
            console.error('Error fetching organization details from ArcGIS:', error);
            return null;
        }
    }

    private async fetchLayerThumbnail(serviceUrl: string, layerId: number): Promise<string | null> {
        const token = sessionStorage.getItem(GIS_USER_TOKEN);
        if (!token) return null;

        try {
            const response = await fetch(`${serviceUrl}/${layerId}?${ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN}=${token}&${ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT}=${ARCGIS_CONSTANTS.FORMAT.JSON}`);
            const data = await response.json();

            const imageData = data.drawingInfo?.renderer?.symbol?.imageData;
            if (imageData) {
                return `data:image/png;base64,${imageData}`;
            }
            return null;
        } catch (error) {
            console.error(`Error fetching thumbnail for layer ${layerId}:`, error);
            return null;
        }
    }

    public async fetchWebMapData(webMapId: string, portalUrl?: string): Promise<ArcGISWebMapData> {
        if (!portalUrl) {
            portalUrl = this.getPortalUrl();
        }
        const token = sessionStorage.getItem(GIS_USER_TOKEN);

        try {
            const response = await fetch(
                `${portalUrl}/sharing/rest/content/items/${webMapId}/data?${ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN}=${token}&${ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT}=${ARCGIS_CONSTANTS.FORMAT.JSON}`
            );
            return await response.json();
        } catch (error) {
            console.error(`Error fetching web map data for ${webMapId}:`, error);
            return null;
        }
    }

    private async fetchFeatureServiceLayers(serviceUrl: string): Promise<ArcGISServiceMetadata> {
        const token = sessionStorage.getItem(GIS_USER_TOKEN);

        try {
            const response = await fetch(`${serviceUrl}?${ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN}=${token}&${ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT}=${ARCGIS_CONSTANTS.FORMAT.JSON}`);
            return await response.json();
        } catch (error) {
            console.error(`Error fetching feature service layers:`, error);
            return null;
        }
    }

    private async fetchOrganizationContent(orgId: string): Promise<ArcGISOrganizationContent> {
        if (!this.isValidArcGISToken()) {
            return null;
        }

        const token = sessionStorage.getItem(GIS_USER_TOKEN);
        const portalUrl = this.getPortalUrl();
        const orgName = await this.fetchOrganizationDetails(orgId);

        const featureServices: ArcGISFeatureService[] = [];
        const webMaps: ArcGISWebMap[] = [];
        const mapServices: ArcGISMapService[] = [];
        let totalFeatureServices = 0;
        let totalWebMaps = 0;
        let totalMapServices = 0;

        try {
            const searchUrl = `${portalUrl}/sharing/rest/search`;
            const pageSize = 100;
            let start = 1;
            let hasMore = true;

            while (hasMore) {
                const params = new URLSearchParams({
                    q: `orgid:${orgId} AND (type:"${ARCGIS_CONSTANTS.SERVICE_TYPES.FEATURE_SERVICE}" OR type:"${ARCGIS_CONSTANTS.SERVICE_TYPES.WEB_MAP}" OR type:"${ARCGIS_CONSTANTS.SERVICE_TYPES.MAP_SERVICE}")`,
                    f: ARCGIS_CONSTANTS.FORMAT.JSON,
                    token: token,
                    num: pageSize.toString(),
                    start: start.toString(),
                    sortField: 'modified',
                    sortOrder: 'desc'
                });

                const response = await fetch(`${searchUrl}?${params}`);
                const data: ArcGISSearchResponse = await response.json();

                if (data.results) {
                    const filteredResults = data.results.filter(item => !item.origin || item.contentStatus === ARCGIS_CONSTANTS.CONTENT_STATUS.PUBLIC_AUTHORITATIVE);
                    for (const item of filteredResults) {
                        if (item.type === ARCGIS_CONSTANTS.SERVICE_TYPES.FEATURE_SERVICE) {
                            featureServices.push(item as ArcGISFeatureService);
                            totalFeatureServices++;
                        } else if (item.type === ARCGIS_CONSTANTS.SERVICE_TYPES.WEB_MAP) {
                            webMaps.push(item as ArcGISWebMap);
                            totalWebMaps++;
                        } else if (item.type === ARCGIS_CONSTANTS.SERVICE_TYPES.MAP_SERVICE) {
                            mapServices.push(item as ArcGISMapService);
                            totalMapServices++;
                        }
                    }
                }

                hasMore = data.nextStart > 0;
                start = data.nextStart || (start + pageSize);
            }

            return {
                featureServices,
                webMaps,
                mapServices,
                totalFeatureServices,
                totalWebMaps,
                totalMapServices,
                organizationId: orgId,
                organizationName: orgName
            };

        } catch (error) {
            console.error('Error fetching organization content:', error);
            return {
                featureServices: [],
                webMaps: [],
                mapServices: [],
                totalFeatureServices: 0,
                totalWebMaps: 0,
                totalMapServices: 0,
                organizationId: orgId,
                organizationName: orgName
            };
        }
    }

    public async searchOrganizationContent(params: ArcGISSearchParams): Promise<ArcGISPaginatedSearchResult> {
        const { orgId, searchTerm = '', start = 1, pageSize = 200 } = params;

        if (!this.isValidArcGISToken()) {
            return {
                assets: [],
                total: 0,
                nextStart: -1,
                hasMore: false,
                searchTerm
            };
        }

        const token = sessionStorage.getItem(GIS_USER_TOKEN);
        const portalUrl = this.getPortalUrl();

        try {
            const searchUrl = `${portalUrl}/sharing/rest/search`;
            const typeFilter = `(type:"${ARCGIS_CONSTANTS.SERVICE_TYPES.FEATURE_SERVICE}" OR type:"${ARCGIS_CONSTANTS.SERVICE_TYPES.WEB_MAP}" OR type:"${ARCGIS_CONSTANTS.SERVICE_TYPES.MAP_SERVICE}")`;

            let queryString = `orgid:${orgId} AND ${typeFilter}`;

            if (searchTerm && searchTerm.trim()) {
                const escapedTerm = searchTerm.trim().replace(/"/g, '\\"');
                queryString = `orgid:${orgId} ${escapedTerm} AND ${typeFilter}`;
            }

            const searchParams = new URLSearchParams({
                q: queryString,
                f: ARCGIS_CONSTANTS.FORMAT.JSON,
                token: token,
                num: pageSize.toString(),
                start: start.toString(),
                sortField: 'modified',
                sortOrder: 'desc'
            });

            const response = await fetch(`${searchUrl}?${searchParams}`);
            const data: ArcGISSearchResponse = await response.json();

            const assets: Array<ArcGISFeatureService | ArcGISWebMap | ArcGISMapService> = data.results || [];
            const filteredAssets = assets.filter(item => !item.origin || item.contentStatus === ARCGIS_CONSTANTS.CONTENT_STATUS.PUBLIC_AUTHORITATIVE);

            return {
                assets: filteredAssets,
                total: filteredAssets.length,
                nextStart: data.nextStart || -1,
                hasMore: data.nextStart > 0,
                searchTerm
            };

        } catch (error) {
            console.error('Error searching organization content:', error);
            return {
                assets: [],
                total: 0,
                nextStart: -1,
                hasMore: false,
                searchTerm
            };
        }
    }

    public async loadAllFeatureLayers(): Promise<void> {
        if (!this.isValidArcGISToken()) {
            this.featureLayersChanged.next([]);
            return;
        }

        const userInfoFetched: boolean = await this.fetchAndStoreUserInfo();
        if (!userInfoFetched) {
            this.featureLayersChanged.next([]);
            return;
        }

        const orgId: string | null = sessionStorage.getItem(ARCGIS_USER_ORG_ID);

        if (orgId) {
            const orgName = await this.fetchOrganizationDetails(orgId);

            this.organizationContentChanged.next({
                featureServices: [],
                webMaps: [],
                mapServices: [],
                totalFeatureServices: 0,
                totalWebMaps: 0,
                totalMapServices: 0,
                organizationId: orgId,
                organizationName: orgName
            });
        }

    }

    public getStoredUserInfo(): { username: string; fullName: string; orgId: string; orgName: string } {
        return {
            username: sessionStorage.getItem(ARCGIS_USERNAME) || '',
            fullName: sessionStorage.getItem(ARCGIS_USER_FULLNAME) || '',
            orgId: sessionStorage.getItem(ARCGIS_USER_ORG_ID) || '',
            orgName: sessionStorage.getItem(ARCGIS_ORG_NAME) || ''
        };
    }
}
